import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { clientStorage } from "@/lib/platform/clientStorage";
import { isShowCatalogCard } from "@/lib/shows/showCardFinding";
import { MAX_HUNT_CARDS, type ShowHuntStop } from "@/lib/shows/showHunt";
import { isRecord } from "@/lib/ui/validatedJson";

export type ShowHuntSnapshot = {
  savedAt: string;
  stops: ShowHuntStop[];
};

const storageKey = (showId: string) => `trovie.show-hunt-route.${encodeURIComponent(showId)}`;
const queryKey = (showId: string | undefined) => ["show-hunt-route-snapshot", showId] as const;
const stringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

function isStop(value: unknown): value is ShowHuntStop {
  if (!isRecord(value) || !isRecord(value.vendor) || !Array.isArray(value.cards)) return false;
  return typeof value.vendor.participation_id === "string"
    && typeof value.vendor.name === "string"
    && stringArray(value.vendor.table_numbers)
    && value.cards.length <= MAX_HUNT_CARDS
    && value.cards.every(isShowCatalogCard);
}

function decode(value: string): ShowHuntSnapshot | null {
  try {
    const parsed: unknown = JSON.parse(value);
    if (
      !isRecord(parsed)
      || typeof parsed.savedAt !== "string"
      || !Number.isFinite(Date.parse(parsed.savedAt))
      || !Array.isArray(parsed.stops)
      || parsed.stops.length > MAX_HUNT_CARDS
      || !parsed.stops.every(isStop)
      || parsed.stops.reduce((count, stop) => count + stop.cards.length, 0) > MAX_HUNT_CARDS
    ) return null;
    return { savedAt: parsed.savedAt, stops: parsed.stops };
  } catch {
    return null;
  }
}

export async function readShowHuntSnapshot(showId: string): Promise<ShowHuntSnapshot | null> {
  try {
    const value = await clientStorage.getItem(storageKey(showId));
    return value === null ? null : decode(value);
  } catch {
    return null;
  }
}

export async function saveShowHuntSnapshot(
  showId: string,
  stops: ShowHuntStop[],
): Promise<ShowHuntSnapshot | null> {
  const snapshot = { savedAt: new Date().toISOString(), stops } satisfies ShowHuntSnapshot;
  try {
    await clientStorage.setItem(storageKey(showId), JSON.stringify(snapshot));
    return snapshot;
  } catch {
    return null;
  }
}

/**
 * A named, derived offline artifact; the live inventory queries remain network-owned.
 * The one inventory-derived thing a device may keep — ADR-0093, the named-query exception
 * ADR-0077 §4 requires be documented before anything is persisted.
 */
export function useShowHuntSnapshot(showId: string | undefined) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: queryKey(showId),
    enabled: !!showId,
    queryFn: () => readShowHuntSnapshot(showId!),
    networkMode: "always",
    retry: false,
    staleTime: Infinity,
  });
  const save = useCallback(async (stops: ShowHuntStop[]) => {
    if (!showId) return null;
    const snapshot = await saveShowHuntSnapshot(showId, stops);
    if (snapshot) queryClient.setQueryData(queryKey(showId), snapshot);
    return snapshot;
  }, [queryClient, showId]);

  return { snapshot: query.data ?? null, hydrated: !query.isPending, save };
}
